import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login, loginWithPhone, confirmOTP } = useAuth();

  // ── Mode ──────────────────────────────────────
  const [mode, setMode] = useState("email"); // "email" | "phone"

  // ── Email login state ─────────────────────────
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");

  // ── Phone login state ─────────────────────────
  const [phone,          setPhone]          = useState("");
  const [otp,            setOtp]            = useState("");
  const [otpSent,        setOtpSent]        = useState(false);
  const [otpLoading,     setOtpLoading]     = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [resendTimer,    setResendTimer]    = useState(0);

  // ── Shared state ──────────────────────────────
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  const recaptchaRef = useRef(null);
  const timerRef     = useRef(null);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(t => t - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [resendTimer]);

  // ── EMAIL LOGIN ───────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);
      const user = await login(email.trim().toLowerCase(), password.trim());
      if (user?.role === "technician") {
        window.location.href = "/technician";
      } else {
        window.location.href = "/";
      }
    } catch (err) {
      setError(err?.message || "Login failed. Check credentials.");
    } finally {
      setLoading(false);
    }
  };

  // ── SEND OTP ──────────────────────────────────
  const handleSendOTP = async () => {
    const cleaned = phone.trim().replace(/\s/g, "");
    if (!cleaned || cleaned.length < 10) {
      setError("Enter valid 10-digit mobile number");
      return;
    }
    const fullPhone = cleaned.startsWith("+") ? cleaned : `+91${cleaned}`;
    try {
      setError("");
      setOtpLoading(true);
      await loginWithPhone(fullPhone, "recaptcha-container");
      setOtpSent(true);
      setResendTimer(30);
    } catch (err) {
      console.error("Send OTP error:", err);
      setError(err?.message || "Failed to send OTP. Try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // ── CONFIRM OTP ───────────────────────────────
  const handleConfirmOTP = async () => {
    if (!otp.trim() || otp.trim().length !== 6) {
      setError("Enter 6-digit OTP");
      return;
    }
    try {
      setError("");
      setConfirmLoading(true);
      await confirmOTP(otp.trim());
      window.location.href = "/technician";
    } catch (err) {
      console.error("Confirm OTP error:", err);
      setError(err?.message || "Invalid OTP. Try again.");
    } finally {
      setConfirmLoading(false);
    }
  };

  // ── RESEND OTP ────────────────────────────────
  const handleResend = async () => {
    setOtp("");
    setOtpSent(false);
    setError("");
    await handleSendOTP();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 p-4">
      <div className="bg-white shadow-2xl rounded-2xl p-8 w-full max-w-md">

        {/* BRAND */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-tr from-cyan-400 to-teal-500 flex items-center justify-center shadow-lg">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M5 6H19L7 18H19" stroke="black" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-slate-800">ZENO</h1>
<p className="text-sm text-gray-500 mt-1">HVAC Operating System</p>
        </div>

        {/* MODE TOGGLE */}
        <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => { setMode("email"); setError(""); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              mode === "email"
                ? "bg-white text-slate-800 shadow"
                : "text-slate-500 hover:text-slate-700"
            }`}>
            📧 Email Login
          </button>
          <button
            onClick={() => { setMode("phone"); setError(""); setOtpSent(false); setOtp(""); }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
              mode === "phone"
                ? "bg-white text-slate-800 shadow"
                : "text-slate-500 hover:text-slate-700"
            }`}>
            📱 Phone OTP
          </button>
        </div>

        {/* ERROR */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl text-sm mb-4">
            {error}
          </div>
        )}

        {/* ── EMAIL FORM ── */}
        {mode === "email" && (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Email</label>
              <input
                type="email" required autoComplete="email"
                value={email} onChange={e => setEmail(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Password</label>
              <input
                type="password" required autoComplete="current-password"
                value={password} onChange={e => setPassword(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400"
                placeholder="Enter password"
              />
            </div>
            <button type="submit" disabled={loading}
              className={`w-full py-3 rounded-xl font-semibold text-white transition text-sm ${
                loading ? "bg-gray-300 cursor-not-allowed" : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90"
              }`}>
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        {/* ── PHONE OTP FORM ── */}
        {mode === "phone" && (
          <div className="space-y-4">

            {/* Phone input */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">
                Mobile Number
              </label>
              <div className="flex gap-2">
                <span className="flex items-center px-3 py-3 bg-slate-100 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600">
                  🇮🇳 +91
                </span>
                <input
                  type="tel" maxLength={10}
                  value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                  disabled={otpSent}
                  className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400 disabled:bg-slate-50"
                  placeholder="10-digit number"
                />
              </div>
            </div>

            {/* Recaptcha container */}
            <div id="recaptcha-container" ref={recaptchaRef} />

            {/* Send OTP button */}
            {!otpSent && (
              <button
                onClick={handleSendOTP}
                disabled={otpLoading || phone.length < 10}
                className={`w-full py-3 rounded-xl font-semibold text-white text-sm transition ${
                  otpLoading || phone.length < 10
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-90"
                }`}>
                {otpLoading ? "Sending OTP..." : "Send OTP"}
              </button>
            )}

            {/* OTP input + confirm */}
            {otpSent && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 font-medium">
                  ✅ OTP sent to +91 {phone}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">
                    Enter OTP
                  </label>
                  <input
                    type="tel" maxLength={6}
                    value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-cyan-400 tracking-widest text-center text-lg font-bold"
                    placeholder="• • • • • •"
                    autoFocus
                  />
                </div>

                <button
                  onClick={handleConfirmOTP}
                  disabled={confirmLoading || otp.length !== 6}
                  className={`w-full py-3 rounded-xl font-semibold text-white text-sm transition ${
                    confirmLoading || otp.length !== 6
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-500 to-teal-500 hover:opacity-90"
                  }`}>
                  {confirmLoading ? "Verifying..." : "Verify & Login"}
                </button>

                {/* Resend */}
                <div className="text-center">
                  {resendTimer > 0 ? (
                    <p className="text-xs text-slate-400">Resend OTP in {resendTimer}s</p>
                  ) : (
                    <button onClick={handleResend}
                      className="text-xs text-cyan-600 font-semibold hover:underline">
                      Resend OTP
                    </button>
                  )}
                </div>
              </>
            )}

            <p className="text-xs text-slate-400 text-center">
              Phone login is for Technicians only
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
